import React, { FC, useEffect, useState } from 'react';
import { Box, Button, Divider, LinearProgress, Typography, useTheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import PlatformService from '../../services/PlatformService';
import { WidgetNoData } from './WidgetNoData';
import { constants } from '../../constants/constants';
import http from '../../shared/HttpCommon';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import { IMonitoring } from '../../shared/interfaces/IMonitoring';
import PlatformImage from '../Logo/PlatformImage';
import CommentingService, { FilterOptions } from '../../services/CommentingService';

interface IPlatformReviewCount {
  platform: string;
  count: number;
}

const WidgetOpenComments: FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [platformReviewCounts, setPlatformReviewCounts] = useState<IPlatformReviewCount[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const { t } = useTranslation();
  const [platformLabels, setPlatformLabels] = useState<any[]>([]);
  const [timeFilter, setTimeFilter] = useState(() => {
    // Try to get the saved filter from localStorage, default to 'week' if not found
    if (typeof window !== 'undefined') {
      const savedFilter = localStorage.getItem('repWidgetOpenCommentsFilter');
      return savedFilter || 'week';
    }
    return 'week';
  });

  const handleTimeFilterChange = (value: string) => {
    setTimeFilter(value);
    // Save the filter to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('repWidgetOpenCommentsFilter', value);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchPlatforms = async () => {
      try {
        const platforms = await PlatformService.getPlatforms(setLoading, controller.signal);
        const monitorings: IMonitoring[] = await ReviewMonitoringService.getMonitoring(
          false,
          'reviews',
          setLoading,
          controller.signal
        );

        const platformLabels: any = [];
        for (const monitoring of monitorings) {
          const platformLabel = PlatformService.getPlatformName(monitoring.platformId, platforms);
          if (platformLabel && !platformLabels.includes(platformLabel)) {
            platformLabels.push(platformLabel);
          }
        }

        if (platformLabels) {
          setPlatformLabels(platformLabels);
        }
      } catch (error) {
        console.error('Error fetching platforms:', error);
      }
    };

    fetchPlatforms();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchReviewsWithoutCompanyComment = async () => {
      setLoading(true);
      if (platformLabels.length === 0) return;
      let totalCount = 0;

      const now = new Date();
      let filterDate = new Date();

      switch (timeFilter) {
        case 'day':
          filterDate.setDate(now.getDate() - 1); // 24 hours
          break;
        case 'month':
          filterDate.setDate(now.getDate() - 30); // 30 days
          break;
        case 'week':
        default:
          filterDate.setDate(now.getDate() - 7); // 7 days
          break;
      }

      try {
        // Client-side filtering approach since there is no dedicated endpoint
        const platformCounts: IPlatformReviewCount[] = [];

        for (const platformLabel of platformLabels) {
          const filterWithout: FilterOptions = {
            withoutComments: true,
            withComments: false,
            platform: platformLabel,
            pageSize: 1,
            startDate: filterDate.toISOString(),
            offset: 0,
            score: [1, 5],
            withGmb: true,
            withMonitoring: true,
            type: 'MONITORINGS',
          };
          const data: any = await CommentingService.getAllReviews(controller, filterWithout);
          totalCount += data.paging.totalItems;
          if (data.paging.totalItems > 0) {
            platformCounts.push({ platform: platformLabel, count: data.paging.totalItems });
          }
        }
        setPlatformReviewCounts(platformCounts);
        setTotalCount(totalCount);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setLoading(false);
      }
    };

    if (platformLabels.length > 0) {
      fetchReviewsWithoutCompanyComment();
    }

    return () => {
      controller.abort();
    };
  }, [platformLabels, timeFilter]);

  return (
    <Box>
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                variant={timeFilter === 'day' ? 'contained' : 'outlined'}
                onClick={() => handleTimeFilterChange('day')}
                sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
              >
                {t('widget_surveytool_filter_day')}
              </Button>
              <Button
                size="small"
                variant={timeFilter === 'week' ? 'contained' : 'outlined'}
                onClick={() => handleTimeFilterChange('week')}
                sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
              >
                {t('widget_surveytool_filter_week')}
              </Button>
              <Button
                size="small"
                variant={timeFilter === 'month' ? 'contained' : 'outlined'}
                onClick={() => handleTimeFilterChange('month')}
                sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
              >
                {t('widget_surveytool_filter_month')}
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mt: theme.spacing(2), mb: theme.spacing(2) }} />
          {platformReviewCounts.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {platformReviewCounts.map((item) => (
                  <>
                    <Box
                      key={item.platform}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        minHeight: 50,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '80px' }}>
                        <PlatformImage
                          src={constants.platform_logos[item.platform]}
                          alt={`${item.platform} ${t('platform_logo')}`}
                        />
                      </Box>
                      <Typography variant="body1">
                        <Trans
                          i18nKey="widget_opencomments_comments_without"
                          components={{ b: <b /> }}
                          values={{ count: item.count }}
                        ></Trans>
                      </Typography>
                    </Box>
                  </>
                ))}
              </Box>
            </Box>
          ) : (
            <WidgetNoData />
          )}
        </>
      )}
    </Box>
  );
};

export default WidgetOpenComments;
